import React from "react";
import { DispatchProp } from "react-redux";
import { BaseControl, ControlProps } from "./BaseControl";
import { QuestionModel } from "../../models/QuestionModel";
import { ChoiceModel } from "../../models/ChoiceModel";
import {
    getSettingBoolean,
    getSettingString,
    getSettingNumber,
} from "../../models/utilities/settingUtilities";
import { isNullOrEmpty, removeHtmlTags } from "../../utils";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { ValidationBuilder } from "../../models/builders/ValidationBuilder";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import { translate } from "../../models/utilities/translationUtilities";
import AutoComplete from "../select/AutoComplete";
import { size } from "../../models/utilities/questionUtilities";
import { isVisible } from "../../models/utilities/choiceUtilities";
import { ElementWithSettings } from "../../models/ElementWithSettings";
import { Piping } from "../../piping";
import { __getStore__ } from "../../redux/store";
import { RecallType } from "../../piping/RecallType";

interface Props extends ControlProps, DispatchProp {}

interface State {
    value: string;
    focus: boolean;
    input?: HTMLElement;
}

export class AutoCompleteControl extends BaseControl<Props, State> {
    input?: HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            value: "",
            focus: false,
        };
    }

    getChoiceText(choice: ChoiceModel, lang?: string) {
        return removeHtmlTags(translate(choice, "TEXT", lang || this.props.language));
    }

    isDefault(choice: ChoiceModel) {
        return choice && !isNullOrEmpty(choice.value) && getSettingBoolean(choice, "Default");
    }

    initializeQuestion(question: QuestionModel, props: Props) {
        super.initializeQuestion(question, props);
        if (!this.hasMentions(props)) {
            this.setDefaultChoices();
        }
        this.updateState(props);
    }

    willUpdateMentions(question: QuestionModel, nextProps: Props) {
        super.willUpdateMentions(question, nextProps);
        this.updateState(nextProps);
    }

    componentDidMount() {
        this.setState((prevState) => ({ ...prevState, input: this.input }));
    }

    hasAnswerWithValue(props: Props, value: string) {
        if (isNullOrEmpty(value)) {
            return false;
        }
        return props.sequencedVariables[0].choiceList.some((choice) => choice.value === value);
    }

    maxValue = () => 1;

    setChoicesAsMentions = (element: ElementWithSettings, choices: ChoiceModel[]) => {
        const values = choices
            .slice(0, this.maxValue() || choices.length)
            .map((choice) => ({ value: choice.value }));
        if (values.length) {
            this.setMentions(
                this.props,
                element.name,
                (mentions) => mentions.clear().add(values),
                undefined,
                true,
            );
        }
    };

    setDefaultChoices = () => {
        const { question, sequencedVariables } = this.props;
        const [variable] = sequencedVariables;
        const { choiceList = [] } = variable;
        const [firstDefaultVisibleChoice] = choiceList.filter(
            (choice) => this.isDefault(choice) && isVisible(choice),
        );
        if (firstDefaultVisibleChoice) {
            this.setChoicesAsMentions(question, [firstDefaultVisibleChoice]);
        }
    };

    updateState(props: Props) {
        this.setState((prevState) => {
            const mention = this.getMention(props);

            const newState = { ...prevState };
            if (!mention) {
                newState.value = "";
                return newState;
            }

            const { value, openEnd } = mention;
            const [variable] = props.question.variables;
            const selectedChoice = variable.choiceList.find(
                (choice) => !isNullOrEmpty(choice.value) && choice.value === value,
            );

            if (!isNullOrEmpty(openEnd)) {
                newState.value = openEnd;
            } else if (
                selectedChoice &&
                this.shouldDisplayMentionValue(variable, selectedChoice.value)
            ) {
                newState.value = this.getChoiceText(selectedChoice, props.language);
            }

            return newState;
        });
    }

    onChange = (value: string) => this.setState({ value });

    onOptionSelected = (value: string) =>
        this.setState({ value }, () => {
            this.onBlur();
        });

    onBlur = () => {
        const { question } = this.props;
        const [variable] = question.variables;
        const state = { ...this.state, focus: false };
        const { value } = state;

        const answer = this.getAutoCompleteAnswer(this.props, value);
        const invalidAsOpenEnd = getSettingBoolean(question, "InvalidAsOpenEnd");

        if (!isNullOrEmpty(value?.trim()) && answer === undefined && invalidAsOpenEnd) {
            const openEndAnswerCode = getSettingString(question, "OpenEndAnswerCode", "0");
            this.setMentions(this.props, variable.name, (mentions) =>
                mentions.set(openEndAnswerCode, value.trim()),
            );
        } else if (answer !== undefined) {
            this.setMentions(this.props, variable.name, (mentions) => mentions.set(answer.value));
        } else if (answer === undefined) {
            state.value = "";
            this.setMentions(this.props, variable.name, (mentions) => mentions.clear());
        }

        this.setState(state);
    };

    getAutoCompleteAnswer(props: Props, value: string) {
        const trimmedLowerValue = value.trim().toLowerCase();
        return props.sequencedVariables[0].choiceList.find(
            (choice) => this.getChoiceText(choice).trim().toLowerCase() === trimmedLowerValue,
        );
    }

    getValue() {
        const value = this.state.value.trim().toLowerCase();
        const answer = this.getAutoCompleteAnswer(this.props, value);
        const invalidAsOpenEnd = getSettingBoolean(this.props.question, "InvalidAsOpenEnd");
        if (value !== "" && answer === undefined && invalidAsOpenEnd) {
            return getSettingString(this.props.question, "OpenEndAnswerCode", "0");
        }
        if (answer && answer.value) {
            return answer.value;
        }
        return "";
    }

    validate(options?: ValidationOptionsModel): ValidationResultModel {
        return new ValidationBuilder().build();
    }

    getRecalledLabel(label: string): string {
        const { interview } = __getStore__().getState();
        return Piping.evaluateRecalls(interview, label, RecallType.Text);
    }

    renderQuestion(question: QuestionModel) {
        const { theme, language, sequencedVariables } = this.props;
        const { value } = this.state;
        const minLength = getSettingNumber(question, "MinSearchLength", 0);
        const maxResults = getSettingNumber(question, "MaxResults", 50);
        const maxResultsMessage = translate(theme, "AutoCompleteMaxResults", language);
        const searchMethod = getSettingString(question, "SearchOption") as
            | "Exact"
            | "StartsWith"
            | "Contains";

        const [variable] = sequencedVariables;
        const options = variable.choiceList.map((choice) => {
            const translatedLabel = translate(choice, "Text", language);
            return {
                id: choice.elementId,
                value: choice.value,
                label: translatedLabel,
                recalledLabel: this.getRecalledLabel(translatedLabel),
            };
        });

        return (
            <AutoComplete
                id={`${variable.elementId}_INPUT`}
                labelId={`${question.elementId}_LABEL`}
                options={options}
                minLength={minLength}
                maxResults={maxResults}
                maxResultsMessage={maxResultsMessage}
                searchMethod={searchMethod}
                value={this.getRecalledLabel(value)}
                onChange={this.onChange}
                onBlur={this.onBlur}
                size={size(question)}
                onOptionSelected={this.onOptionSelected}
            />
        );
    }
}
